import { BookOpen, LogOut, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '../auth/context';
import { BrandMark } from './BrandMark';
import { HelpLauncher } from '../helpchat/HelpLauncher';
import { ErrorBoundary } from './ErrorBoundary';
import { HelpPageProvider } from '../helpchat/HelpPageProvider';
import { applyTheme, storedTheme, type ConsoleTheme } from '../lib/theme';
import { CapabilitiesProvider } from './CapabilitiesProvider';
import { TenantTimeProvider } from './TenantTimeProvider';

const TABS = [
  { to: '/dashboard', label: 'Dashboard', end: false },
  { to: '/comms', label: 'Communications', end: false },
  { to: '/', label: 'Channels', end: true },
  { to: '/bot', label: 'Chatbot', end: false },
  { to: '/ai', label: 'AI', end: false },
];

/** Signed-in chrome: top bar with wordmark, section tabs, tenant chip, sign out. */
export function AppLayout() {
  const location = useLocation();
  const { tenant, email, signOut } = useAuth();
  const [theme, setTheme] = useState<ConsoleTheme>(storedTheme);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
  };

  return (
    <div className="shell">
      <header className="topbar">
        <div className="wordmark">
          <BrandMark className="wordmark-badge" />
          LimoSphere CRM
        </div>
        <nav className="topbar-tabs" aria-label="Sections">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => (isActive ? 'topbar-tab active' : 'topbar-tab')}
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
        <div className="topbar-right">
          {/* Reference, not a section — so it sits with the utilities rather
              than in the tab strip. It documents the dispatch dashboard too. */}
          <Link
            to="/help/docs"
            className="btn btn-ghost"
            title="Handbook — every screen and control, written out"
          >
            <BookOpen size={14} />
            Handbook
          </Link>
          <button
            className="btn btn-ghost"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <span className="tenant-chip" title={email ?? undefined}>
            {tenant}
          </span>
          <button className="btn btn-ghost" onClick={() => void signOut()}>
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </header>
      {/* Every screen below renders clock times in the tenant's dispatch zone,
          and gates its controls on the signed-in agent's grants. */}
      {/* Wraps BOTH the screens and the launcher: screens publish what they
          are showing, the launcher sends it with the question. */}
      <HelpPageProvider>
        <TenantTimeProvider>
          <CapabilitiesProvider>
            {/* A crash in one screen must not take the console with it — and
                the launcher below stays mounted, so the agent can still ask
                what happened. */}
            {/* Keyed on the route: a boundary holds its error until something
                remounts it, so without this an agent who crashed one screen
                would carry the apology into every screen they moved to. */}
            <ErrorBoundary key={location.pathname} label="This screen">
              <Outlet />
            </ErrorBoundary>
          </CapabilitiesProvider>
        </TenantTimeProvider>
        <HelpLauncher />
      </HelpPageProvider>
    </div>
  );
}
